import React, { useState } from "react";
import styled from "styled-components";
import { Order } from "../../api/orderService";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 1rem;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #f0eaea;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #faf9f9;
  border-radius: 1rem 1rem 0 0;
`;

const ModalTitle = styled.h2`
  color: #e97d7d;
  margin: 0;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: #333;
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  border-bottom: 2px solid #e97d7d;
  padding-bottom: 0.5rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  background: #f9f9f9;
  padding: 1rem;
  border-radius: 0.5rem;
  border-left: 4px solid #e97d7d;
`;

const InfoLabel = styled.div`
  font-weight: 600;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  color: #333;
  font-size: 1rem;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => {
    switch (props.status) {
      case "pending":
        return "#ffc107";
      case "processing":
        return "#17a2b8";
      case "shipped":
        return "#007bff";
      case "delivered":
        return "#28a745";
      case "cancelled":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  }};
  color: #fff;
`;

const ItemsList = styled.div`
  background: #f9f9f9;
  border-radius: 0.5rem;
  padding: 1rem;
`;

const OrderItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #fff;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:last-child {
    margin-bottom: 0;
  }
`;

const ItemImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 0.5rem;
  margin-right: 1rem;
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
`;

const ItemQuantity = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const ItemPrice = styled.div`
  color: #e97d7d;
  font-weight: 600;
  font-size: 1.1rem;
`;

const StatusUpdateSection = styled.div`
  background: #f0f8ff;
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid #e3f2fd;
`;

const StatusForm = styled.form`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const StatusSelect = styled.select`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 2px solid #e0e0e0;
  background: #fff;
  font-size: 1rem;
  min-width: 150px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #e97d7d;
  }
`;

const TrackingInput = styled.input`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 2px solid #e0e0e0;
  background: #fff;
  font-size: 1rem;
  width: 100%;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #e97d7d;
  }
`;

const TrackingTextarea = styled.textarea`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 2px solid #e0e0e0;
  background: #fff;
  font-size: 1rem;
  width: 100%;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #e97d7d;
  }
`;

const UpdateButton = styled.button`
  background: #e97d7d;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #d96a6a;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const TotalSection = styled.div`
  background: #e97d7d;
  color: #fff;
  padding: 1.5rem;
  border-radius: 0.5rem;
  text-align: center;
  margin-top: 1rem;
`;

const TotalAmount = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #fff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

interface OrderDetailsModalProps {
  order: Order | null;
  onClose: () => void;
  onStatusUpdate: (
    orderId: string,
    status: string,
    trackingUrl?: string,
    trackingDetails?: string
  ) => Promise<void>;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  order,
  onClose,
  onStatusUpdate,
}) => {
  const [newStatus, setNewStatus] = useState(order?.status || "");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [trackingDetails, setTrackingDetails] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  if (!order) return null;

  const handleStatusUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedTrackingUrl =
      trackingUrl && trackingUrl.trim() ? trackingUrl.trim() : undefined;
    const normalizedTrackingDetails =
      trackingDetails && trackingDetails.trim()
        ? trackingDetails.trim()
        : undefined;

    if (
      newStatus === order.status &&
      !normalizedTrackingUrl &&
      !normalizedTrackingDetails
    )
      return;

    setIsUpdating(true);
    try {
      await onStatusUpdate(
        order.id,
        newStatus,
        normalizedTrackingUrl,
        normalizedTrackingDetails
      );
      onClose();
    } catch (error) {
      console.error("Failed to update order status:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getStatusOptions = () => {
    const currentStatus = order.status;
    const allStatuses = [
      { value: "pending", label: "Pending" },
      { value: "processing", label: "Processing" },
      { value: "shipped", label: "Shipped" },
      { value: "delivered", label: "Delivered" },
      { value: "cancelled", label: "Cancelled" },
    ];

    // Allow all statuses for now, but you can add business logic here
    return allStatuses;
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Order Details - #{order.id}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* Order Information */}
          <Section>
            <SectionTitle>Order Information</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Order ID</InfoLabel>
                <InfoValue>#{order.id}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  <StatusBadge status={order.status}>
                    {order.status}
                  </StatusBadge>
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Order Date</InfoLabel>
                <InfoValue>{formatDate(order.createdAt)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Total Amount</InfoLabel>
                <InfoValue>
                  ${(order.total || order.totalAmount || 0).toFixed(2)}
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

          {/* Customer Information */}
          <Section>
            <SectionTitle>Customer Information</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Customer Name</InfoLabel>
                <InfoValue>{order.customerName || "N/A"}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{order.customerEmail || "N/A"}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

          {/* Shipping Address */}
          {order.shippingAddress && (
            <Section>
              <SectionTitle>Shipping Address</SectionTitle>
              <InfoItem>
                <InfoValue>
                  <div>
                    <strong>
                      {order.shippingAddress.name || order.customerName}
                    </strong>
                  </div>
                  <div>{order.shippingAddress.address}</div>
                  <div>
                    {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
                    {order.shippingAddress.zipCode}
                  </div>
                  <div>{order.shippingAddress.country}</div>
                </InfoValue>
              </InfoItem>
            </Section>
          )}

          {/* Order Items */}
          <Section>
            <SectionTitle>Order Items</SectionTitle>
            <ItemsList>
              {order.items?.map((item, index) => (
                <OrderItem key={index}>
                  {item.image && <ItemImage src={item.image} alt={item.name} />}
                  <ItemDetails>
                    <ItemName>{item.name}</ItemName>
                    <ItemQuantity>Quantity: {item.quantity}</ItemQuantity>
                  </ItemDetails>
                  <ItemPrice>
                    ${(item.price * item.quantity).toFixed(2)}
                  </ItemPrice>
                </OrderItem>
              ))}
            </ItemsList>
          </Section>

          {/* Status Update */}
          <StatusUpdateSection>
            <SectionTitle>Update Order Status</SectionTitle>
            <StatusForm onSubmit={handleStatusUpdate}>
              <div style={{ width: "100%", marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  Status
                </label>
                <StatusSelect
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  {getStatusOptions().map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </StatusSelect>
              </div>

              <div style={{ width: "100%", marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  Tracking URL <span style={{ color: "#999" }}>(optional)</span>
                </label>
                <TrackingInput
                  type="url"
                  value={trackingUrl}
                  onChange={(e) => setTrackingUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>

              <div style={{ width: "100%", marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  Tracking Details{" "}
                  <span style={{ color: "#999" }}>(optional)</span>
                </label>
                <TrackingTextarea
                  value={trackingDetails}
                  onChange={(e) => setTrackingDetails(e.target.value)}
                  placeholder="Enter tracking details, carrier information, expected delivery date, etc."
                  rows={3}
                />
              </div>

              <UpdateButton
                type="submit"
                disabled={
                  isUpdating ||
                  (newStatus === order.status &&
                    !trackingUrl &&
                    !trackingDetails)
                }
                style={{ width: "100%" }}
              >
                {isUpdating && <LoadingSpinner />}
                {isUpdating ? "Updating..." : "Update Status"}
              </UpdateButton>
            </StatusForm>
          </StatusUpdateSection>

          {/* Total */}
          <TotalSection>
            <div>Total Amount</div>
            <TotalAmount>
              ${(order.total || order.totalAmount || 0).toFixed(2)}
            </TotalAmount>
          </TotalSection>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default OrderDetailsModal;
